// components/PrivateRoute.tsx
import React, { useRef } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { jwtDecode } from 'jwt-decode';
import moment from 'moment';
import Cookies from 'universal-cookie';

import { useIdleTimeout } from '../hooks/useIdleTimeout';
import { isAuthorised, refreshToken, signOut } from '../services/auth';
import { userDetails, isMasterVendor } from '../store';

const cookies = new Cookies();

const PrivateRoute: React.FC = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const setUserDetails = useRecoilState(userDetails)[1];
  const [masterVendor, setMasterVendor] = useRecoilState(isMasterVendor);
  const isUseEffectFired = useRef(false);

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");

  const handleTimeout = async () => {
    try {
      if (currentUser !== "{}") {
        const response = await signOut(currentUser);
        if (response?.data) {
          alert("Session timeout, please login again.");
          cookies.remove("XSRF-TOKEN");
          localStorage.clear();
          setIsLoggedIn(false);
          setIsLoading(true);
          setMasterVendor(false);
          navigate("/auth/sign-in-new");
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const {  } = useIdleTimeout({
    timeout: 1800_000,
    onTimeout: handleTimeout,
  });

  React.useEffect(() => {
    if (!isUseEffectFired.current) {
      isUseEffectFired.current = true;
      isAuthorised(currentUser)
        .then((data) => {
          setIsLoggedIn(true);
          setIsLoading(false);
          setUserDetails(data?.data);
          if (data?.data?.isMasterVendor) {
            localStorage.setItem("isMasterVendor", data?.data?.isMasterVendor);
            setMasterVendor(true);
          }
          localStorage.setItem("lanMandatory", data?.data?.lanMandatory);
        })
        .catch(() => {
          setIsLoggedIn(false);
          setIsLoading(true);
          setMasterVendor(false);
          navigate("/auth/sign-in-new");
        });
    }
  }, [currentUser, navigate, setMasterVendor, setUserDetails]);

  React.useEffect(() => {
    if (currentUser === "{}") return;

    const interval = setInterval(() => {
      const idToken = localStorage.getItem("userToken") || "";
      if (idToken) {
        const decodedToken: any = jwtDecode(idToken);
        const tokenExpiryTime = moment.unix(decodedToken.exp);
        const currentTime = moment();

        if (tokenExpiryTime.diff(currentTime, "minutes") <= 10) {
          refreshToken(currentUser)
            .then((response: any) => {
              if (response?.data) {
                localStorage.setItem("userToken", response.data.token);
              }
            })
            .catch((error) => console.error(error));
        }
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [currentUser]);

  const userKey = localStorage.getItem("userKey");
  const userToken = localStorage.getItem("userToken");

  if (!userKey || !userToken) {
    return <Navigate to="/auth/sign-in-new" replace />;
  }

  return isLoading ? null : isLoggedIn ? <Outlet /> : <Navigate to="/auth/sign-in-new" />;
};

export default PrivateRoute;